import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Info } from 'lucide-react';
import { addAttendee, EventPhase } from '../api';
import { listMembers, MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { useToast } from '../../../context/ToastContext';

export default function AttendeeForm({ eventId, phases = [], onClose, onSaved }: {
  eventId: number; phases?: EventPhase[]; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [picked, setPicked] = useState<MemberRow | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MemberRow[]>([]);
  const [openList, setOpenList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qty, setQty] = useState(1);
  const activePhases = phases.filter((p) => p.active);
  const [phaseId, setPhaseId] = useState<number | ''>(activePhases.length === 1 ? activePhases[0].phase_id : '');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (picked || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      listMembers({ search: q.trim(), page_size: 8 })
        .then((r) => { setResults(r.data); setOpenList(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, picked]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpenList(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!picked) { toast.error('Selecciona un alumno'); return; }
    setSaving(true);
    try {
      const r = await addAttendee(eventId, picked.account_id, qty, phaseId === '' ? null : phaseId);
      toast.success(r.status === 'waitlist'
        ? `Sin cupo: ${qty} boleto(s) en lista de espera`
        : `${qty} boleto(s) registrado(s)`);
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo registrar');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md overflow-visible rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Registrar asistente</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div ref={boxRef} className="relative">
            <label className="mb-1 block text-xs font-medium text-content-2">Alumno</label>
            {picked ? (
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
                <PersonAvatar contactId={picked.account_id} hasPhoto={picked.has_photo}
                  name={picked.firstname} lastName={picked.lastname}
                  size="h-7 w-7" ringClass="fill-surface-2" />
                <span className="flex-1 text-sm text-content">{picked.firstname} {picked.lastname}</span>
                <button type="button" onClick={() => { setPicked(null); setQ(''); }} className="text-content-3 hover:text-content"><X size={15} /></button>
              </div>
            ) : (
              <>
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpenList(true)}
                  placeholder="Busca por nombre…"
                  className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
                {openList && results.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-line bg-surface shadow-lg">
                    {results.map((m) => (
                      <button type="button" key={m.account_id} onClick={() => { setPicked(m); setOpenList(false); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-2">
                        <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                          name={m.firstname} lastName={m.lastname}
                          size="h-6 w-6" initialClass="text-[10px]" />
                        <span className="text-content">{m.firstname} {m.lastname}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-content-2">Boletos</label>
              <input type="number" min={1} value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
            </div>
            {activePhases.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-medium text-content-2">Fase</label>
                <select value={phaseId} onChange={(e) => setPhaseId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue">
                  <option value="">Automática (por fecha)</option>
                  {activePhases.map((p) => (
                    <option key={p.phase_id} value={p.phase_id}>
                      {p.name}{p.max_per_account ? ` · máx ${p.max_per_account}/alumno` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-line bg-canvas p-2.5 text-xs text-content-3">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>Registra los boletos. Si no hay cupo en la fase, pasan a <strong>lista de espera</strong>. El cobro real se activa en el cutover.</span>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">
              {saving ? 'Guardando…' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
